import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { useMutation } from '@apollo/client';
import { Select, SelectOption, Button, AlertService, ExportIcon, TextInput, Radio } from '@spoiler-alert/ui-library';
import { UpdateAnalyticsExclusion } from './exclusion-mutations';

const exclusionTypeOptions = [
  { value: 'inventoryIds', text: 'Inventory IDs' },
  { value: 'transactionCycleIds', text: 'Transaction Cycle IDs' },
];

const allowedFileTypes = ['.csv'];

const useStyles = createUseStyles({
  selectBlock: {
    maxWidth: 300,
    marginBottom: 10,
  },
  actionBlock: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
  },
  inputBlock: {
    display: 'flex',
    alignItems: 'anchor-center',
    justifyContent: 'space-between',
    marginBottom: 10,
    gap: 20,
  },
  primaryButton: {
    minWidth: 100,
  },
});

const AnalyticsExclusionWidget = () => {
  const classes = useStyles();
  const [selectedExclusionType, setSelectedExclusionType] = useState();
  const [uploadKey, setUploadKey] = useState(0);
  const [fileInput, setFileInput] = useState(null);
  const [tcIds, setTcIds] = useState([]);
  const [action, setAction] = useState({ text: 'Hide', value: true });
  const [updateAnalyticsExclusion, { loading: loadingUpdateExcludedValues }] = useMutation(UpdateAnalyticsExclusion);

  const handleBrowseFilesClick = (ev) => {
    ev.preventDefault();
    fileInput.click();
  };

  const isValidHex24 = (str) => {
    return /^[0-9a-fA-F]{24}$/.test(str);
  };

  const handleInvalidIds = (ids) => {
    const invalidIds = ids.filter((id) => !isValidHex24(id));
    if (invalidIds.length > 0) {
      AlertService.alert({
        type: 'warning',
        autoDismiss: true,
        message: <span>{`Invalid IDs: ${invalidIds.join(', ')}`}</span>,
      });
      return true;
    }
    return false;
  };

  const handleTransactionCycleIds = (ids) => {
    const dataArray = ids.split(',').map((row) => row.trim().replace(/['"]/g, ''));
    setTcIds(dataArray);
  };

  const handleAction = (actionObject) => {
    setAction(actionObject);
  };

  const handleUpdateAnalyticsExclusion = (variables) =>
    updateAnalyticsExclusion({ variables })
      .then(() => {
        AlertService.alert({
          type: 'success',
          autoDismiss: true,
          message: <span>{'Successfully updated exclusions.'}</span>,
        });
      })
      .catch((err) => {
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{`Something went wrong with updating the exclusions.  Err: ${err.message}`}</span>,
        });
      });

  const handleFileChange = (ev) => {
    const file = ev.target.files[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();

    reader.onloadend = () => {
      const text = reader.result;
      const rows = text.trim().split(/\r?\n/);
      const dataArray = rows.map((row) => row.split(',')[0].trim().replace(/['"]/g, ''));
      if (handleInvalidIds(dataArray)) return;
      const variables = {
        inventoryIds: dataArray,
        transactionCycleIds: [],
        action: action?.text?.toLowerCase() || 'hide',
      };
      handleUpdateAnalyticsExclusion(variables);
    };
    // Listener for errors
    reader.onerror = (err) => {
      AlertService.alert({
        type: 'warning',
        autoDismiss: true,
        message: <span>{`Something went wrong with reading the file.  Err: ${err.message}`}</span>,
      });
    };
    // Kicks off the file read
    reader.readAsText(file);
    // Rotating this key resets the file input element
    setUploadKey(uploadKey + 1);
  };

  const handleSave = () => {
    if (handleInvalidIds(tcIds)) return;
    const variables = {
      transactionCycleIds: tcIds,
      inventoryIds: [],
      action: action?.text?.toLowerCase() || 'hide',
    };
    handleUpdateAnalyticsExclusion(variables);
  };

  return (
    <div>
      <div>
        <h4>Analytics Exclusions</h4>
        <div className={classes.actionBlock}>
          <Radio
            selectedItem={action}
            onChange={handleAction}
            options={[
              { text: 'Hide', value: true },
              { text: 'Unhide', value: false },
            ]}
          />
        </div>
        <div className={classes.selectBlock}>
          <Select
            minimal
            placeholderText="Select an Exclusion Type"
            onChange={(e) => setSelectedExclusionType(e[0])}
            selectedItem={selectedExclusionType}
            multiple={false}
          >
            {exclusionTypeOptions.map((type) => (
              <SelectOption key={type.value} value={type.value} className={classes.siteSelectOption}>
                {type.text}
              </SelectOption>
            ))}
          </Select>
        </div>
        {selectedExclusionType?.value === 'inventoryIds' && (
          <Button
            className={classes.primaryButton}
            icon={ExportIcon}
            onClick={handleBrowseFilesClick}
            loading={loadingUpdateExcludedValues}
            loadingText="Uploading..."
            style={{ marginBottom: 8, marginTop: 8 }}
            disabled={!selectedExclusionType}
          >
            Upload Exclusions File
          </Button>
        )}
        {selectedExclusionType?.value === 'transactionCycleIds' && (
          <div className={classes.inputBlock}>
            <TextInput labelText="Transaction cycle IDs separated by comma" onChange={handleTransactionCycleIds} />
            <Button
              onClick={handleSave}
              className={classes.primaryButton}
              disabled={!tcIds.length}
              loading={loadingUpdateExcludedValues}
              loadingText="Saving..."
            >
              Save
            </Button>
          </div>
        )}
        <input
          type="file"
          id="upload-analytics-exclusion"
          key={`upload-${uploadKey}`}
          onChange={handleFileChange}
          accept={allowedFileTypes.join(',')}
          style={{ display: 'none' }}
          ref={(el) => setFileInput(el)}
        />
      </div>
    </div>
  );
};

AnalyticsExclusionWidget.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default AnalyticsExclusionWidget;
